class ShopifyCart {
  constructor() {
    this.endpoints = {
      add: '/cart/add.js',
      change: '/cart/change.js',
      get: '/cart.js'
    };
    
    this.selectors = {
      addToCartForm: '.add-to-cart-rendom',
      cartCount: '.mini-cart-count',
      miniCartContent: '.cartminiwidget',
      miniCartPrice: '.cartminicheckout-price',
      removeItemButton: '.remove_from_cart_button',
      cartTotal: '.woocommerce-mini-cart__total .woocommerce-Price-amount',
      addToCartButton: '.add-product-cart-btn'
    };

    this.moneyFormat = window.Shopify?.money_format || window.theme?.moneyFormat || '{{ amount }}';

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeLoadingSpinners();
  }

  initializeLoadingSpinners() {
    // Add loading spinner to all add to cart buttons
    document.querySelectorAll(this.selectors.addToCartButton).forEach(button => {
      if (!button.querySelector('.loading-icon')) {
        const loadingIcon = document.createElement('div');
        loadingIcon.className = 'loading-icon';
        loadingIcon.style.cssText = 'display: none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);';
        loadingIcon.innerHTML = this.createSpinner();
        button.style.position = 'relative';
        button.appendChild(loadingIcon);
      }
    });
  }

  bindEvents() {
    // Add to cart form submission
    document.addEventListener('submit', (e) => {
      if (e.target.matches(this.selectors.addToCartForm)) {
        e.preventDefault();
        this.handleAddToCart(e.target);
      }
    });

    // Remove item button click
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.removeItemButton)) {
        e.preventDefault();
        const button = e.target;
        const url = button.getAttribute('href');
        if (url) {
          const itemId = this.getItemIdFromUrl(url);
          if (itemId) {
            this.handleRemoveItem(itemId);
          }
        }
      }
    });
  }

  getItemIdFromUrl(url) {
    const match = url.match(/change\?id=([^&]+)/);
    return match ? match[1] : null;
  }

  createSpinner() {
    return `
      <svg class="loading-spinner" viewBox="0 0 50 50" style="width: 20px; height: 20px;">
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="currentColor"
          stroke-width="5"
          style="animation: spin 1s linear infinite;"
        />
      </svg>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .loading-spinner circle {
          stroke-dasharray: 85;
          stroke-dashoffset: 85;
          animation: dash 1.5s ease-in-out infinite;
        }
        @keyframes dash {
          0% { stroke-dashoffset: 85; }
          50% { stroke-dashoffset: 21; }
          100% { stroke-dashoffset: 85; }
        }
      </style>
    `;
  }

  async handleAddToCart(form) {
    const addButton = form.querySelector(this.selectors.addToCartButton);
    const tooltipElement = addButton?.querySelector('.tp-product-tooltip');
    const cartBtnSvg = addButton?.querySelector('.cart_btn_svg');
    const loadingIcon = addButton?.querySelector('.loading-icon');

    // Create and insert spinner if it doesn't exist
    if (!loadingIcon) {
      const spinner = document.createElement('div');
      spinner.className = 'loading-icon';
      spinner.style.display = 'none';
      spinner.innerHTML = this.createSpinner();
      addButton?.appendChild(spinner);
    }

    try {
      // Show loading state
      if (tooltipElement) tooltipElement.textContent = 'Adding...';
      if (cartBtnSvg) cartBtnSvg.style.display = 'none';
      if (loadingIcon) loadingIcon.style.display = 'block';

      const formData = new FormData(form);
      const response = await this.fetchJson('/cart/add', {
        method: 'POST',
        body: formData
      });

      // Update cart state
      if (tooltipElement) tooltipElement.textContent = 'Added to cart';
      await this.updateMiniCart();
      await this.updateCartCount();
      await this.updateCartTotal();

    } catch (error) {
      console.error('Add to cart error:', error);
      if (tooltipElement) tooltipElement.textContent = 'Error adding to cart';
    } finally {
      // Reset button state
      if (cartBtnSvg) cartBtnSvg.style.display = 'block';
      if (loadingIcon) loadingIcon.style.display = 'none';
      setTimeout(() => {
        if (tooltipElement) tooltipElement.textContent = 'Add to cart';
      }, 2000);
    }
  }

  async handleRemoveItem(itemKey) {
    try {
      await this.fetchJson(this.endpoints.change, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      await this.updateMiniCart();
      await this.updateCartCount();
      await this.updateCartTotal();
    } catch (error) {
      console.error('Remove item error:', error);
    }
  }

  async updateCartCount() {
    try {
      const cart = await this.fetchJson(this.endpoints.get);
      const countElements = document.querySelectorAll(this.selectors.cartCount);
      countElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Update cart count error:', error);
    }
  }

  async updateCartTotal() {
    try {
      const cart = await this.fetchJson(this.endpoints.get);
      const totalElements = document.querySelectorAll(this.selectors.cartTotal);
      const formattedTotal = this.formatMoney(cart.total_price);
      
      totalElements.forEach(element => {
        element.innerHTML = formattedTotal;
      });
    } catch (error) {
      console.error('Update cart total error:', error);
    }
  }


  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    return this.moneyFormat
      .replace('{{ amount }}', value)
      .replace('{{amount}}', value)
      .replace('{{ amount_no_decimals }}', Math.floor(value))
      .replace('{{amount_no_decimals}}', Math.floor(value));
  }

  async updateMiniCart() {
    try {
      const response = await fetch('/cart');
      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Update mini cart content
      const miniCartElements = document.querySelectorAll(this.selectors.miniCartContent);
      const newMiniCartContent = tempDiv.querySelector(this.selectors.miniCartContent);
      if (newMiniCartContent) {
        miniCartElements.forEach(element => {
          element.innerHTML = newMiniCartContent.innerHTML;
        });
      }

      // Update mini cart price
      const miniCartPriceElements = document.querySelectorAll(this.selectors.miniCartPrice);
      const newMiniCartPrice = tempDiv.querySelector(this.selectors.miniCartPrice);
      if (newMiniCartPrice) {
        miniCartPriceElements.forEach(element => {
          element.innerHTML = newMiniCartPrice.innerHTML;
        });
      }
    } catch (error) {
      console.error('Update mini cart error:', error);
    }
  }

  async fetchJson(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }
}

// Initialize cart functionality
document.addEventListener('DOMContentLoaded', () => {
  window.shopifyCart = new ShopifyCart();
});
